// src/components/SidebarAsistentes.jsx (Versión Simplificada y Correcta)
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import {
  PanelLeftClose, 
  LogOut,
  Settings,
  UserCircle,
  ChevronDown,
  ChevronRight,
  Plus,
  MessageSquare,
  Edit,
  Trash2,
  MessageSquarePlus,
  Theater,
  Mic,
  LayoutDashboard,
  BarChart3,
  Mail,
  Inbox,
  Send,
  GraduationCap,
  BookOpen,
  House,
  Award,
} from "lucide-react";
import asistentesConfigOrig from '../constants/asistentesConfig';
import * as LucideIcons from 'lucide-react';
import { useUnreadMessages } from '../hooks/useUnreadMessages';

const ChatContextMenuModal = ({ chat, assistantKey, onRename, onDelete, onClose }) => {
  if (!chat) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white p-4 rounded-lg shadow-lg w-64 flex flex-col gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-semibold mb-2 text-gray-800">Opciones para "{chat.nombre || `Chat (${new Date(chat.fechaCreacion).toLocaleDateString()})`}"</p>
        <button
          onClick={onDelete}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-100 rounded-md"
        >
          <Trash2 size={16} /> Eliminar
        </button>
        <button
          onClick={onClose}
          className="mt-4 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md border border-gray-300"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

const getAssistantColorClasses = (color) => {
  switch (color) {
    case 'blue':
      return 'bg-blue-100 text-blue-800';
    case 'red':
      return 'bg-red-100 text-red-800';
    case 'purple':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const SidebarAsistentes = ({
  userName,
  userEmail,
  allUserChats,
  handleSelectAssistantForNewChat,
  handleSelectChat,
  handleDeleteChat,
  handleRenameChat,
  selectedAssistant,
  selectedChatId,
  expanded,
  setExpanded,
  sidebarVisible,
  setSidebarVisible,
  isCreatingNewChat,
  MAX_CHATS_PER_ASSISTANT,
  loading,
  isAuthenticated,
  onBackToDashboard
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isTeacher, user } = useAuth(); // Hook para verificar rol de admin/teacher y obtener user
  const { unreadCount } = useUnreadMessages(); // Hook para mensajes no leídos
  const [hovered, setHovered] = useState({ section: null, id: null });
  
  // TICKET NVDLP4 - Feature flags separados
  // Micro Cursos: Solo admin/teacher o con flag específico
  const microCoursesEnabled = isAdmin || isTeacher || Boolean(user?.flags?.courses_v1);
  
  // Programas Grupales: Disponibles para TODOS los usuarios autenticados
  // Los cursos publicados son públicos, no requieren permisos especiales
  const groupCoursesEnabled = Boolean(user); // Si hay usuario autenticado
  
  const [quickAccess, setQuickAccess] = useState(() => JSON.parse(localStorage.getItem('quickAccess') || '[]'));

  // Verificar accesos rápidos al montar el componente
  useEffect(() => {
    const checkQuickAccess = () => {
      const access = JSON.parse(localStorage.getItem('quickAccess') || '[]');
      setQuickAccess(access);
    };
    
    checkQuickAccess();
    
    // Listener para actualizar accesos rápidos cuando cambien
    const handleStorageChange = (e) => {
      if (e.key === 'quickAccess') {
        checkQuickAccess();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Función para detectar si una sección está activa
  const isActiveSection = (sectionType, assistantKey = null) => {
    const path = location.pathname;
    
    switch (sectionType) {
      case 'dashboard':
        return path === '/' || path === '/dashboard';
      case 'innovafast':
        return path.includes('/innovafast') || assistantKey === 'innovafast';

      case 'eplbuilder':
        return path.includes('/epl') || assistantKey === 'eplbuilder';
      case 'assistant':
        return assistantKey && (path.includes(`/chat/${assistantKey}`) || selectedAssistant === assistantKey);
      default:
        // Para asistentes específicos, verificar si coincide con la clave
        if (assistantKey) {
          return path.includes(`/chat/${assistantKey}`) || path.includes('/epl') || selectedAssistant === assistantKey;
        }
        return false;
    }
  };
  const [editingChatId, setEditingChatId] = useState(null);
  const [newChatName, setNewChatName] = useState("");

  const touchStartTimer = useRef(null);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [selectedChatForContext, setSelectedChatForContext] = useState(null);
  const touchStartX = useRef(0);
  // Permite que el Dashboard controle la vista de Rol Play

  const handleNewChatClick = (assistantKey) => {
    if (loading || !isAuthenticated || isCreatingNewChat || (allUserChats[assistantKey]?.length || 0) >= MAX_CHATS_PER_ASSISTANT || !asistentesConfig[assistantKey]?.canCreateChat) {
      console.log("DEBUG Sidebar: Intento de crear chat bloqueado por isCreatingNewChat, límite, loading, falta de autenticación, o asistente no autorizado para crear chats.");
      return;
    }
    handleSelectAssistantForNewChat(assistantKey);
    if (window.innerWidth < 768) {
      setSidebarVisible(false);
    }
  };

  const handleChatSelection = (chatId, assistantKey) => {
    handleSelectChat(chatId, assistantKey);
    if (window.innerWidth < 768) {
      setSidebarVisible(false);
    }
  };

  // Eliminar código duplicado/no usado en startRename
  const startRename = (chatId, currentName) => {
    setEditingChatId(chatId);
    setNewChatName(currentName);
  };

  const cancelRename = () => {
    setEditingChatId(null);
    setNewChatName("");
  };

  const handleTouchStart = (e, chat, assistantKey) => {
    if (editingChatId === chat._id || showContextMenu) return;

    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;

    touchStartTimer.current = setTimeout(() => {
      setSelectedChatForContext({ chat, assistantKey });
      setShowContextMenu(true);
    }, LONG_PRESS_THRESHOLD_MS);
  };

  const handleTouchMove = (e) => {
    if (!touchStartTimer.current || !e.touches[0]) return;

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const deltaX = Math.abs(currentX - touchStartX.current);
    const deltaY = Math.abs(currentY - touchStartY.current);

    if (deltaX > MOVE_TOLERANCE_PX || deltaY > MOVE_TOLERANCE_PX) {
      clearTimeout(touchStartTimer.current);
      touchStartTimer.current = null;
    }
  };

  const handleTouchEnd = (e, chat, assistantKey) => {
    if (touchStartTimer.current) {
      clearTimeout(touchStartTimer.current);
      touchStartTimer.current = null;

      if (!showContextMenu && editingChatId !== chat._id) {
        handleChatSelection(chat._id, assistantKey);
      }
    }
    if (!showContextMenu) setSelectedChatForContext(null);
  };

  const closeContextMenu = () => {
    setShowContextMenu(false);
    setSelectedChatForContext(null);
  };

  // Preparamos las keys de los asistentes aquí para mantener el JSX limpio
  const asistentesConfig = { ...asistentesConfigOrig };
  const assistantKeys = Object.keys(asistentesConfig);

  return (
    <>
      <style>{`
        @media (max-width: 767px) {
          .sidebar-mobile-fixed {
            position: fixed;
            top: var(--dashboard-header-height-mobile);
            height: calc(100vh - var(--dashboard-header-height-mobile));
            height: calc(100dvh - var(--dashboard-header-height-mobile));
            left: 0;
          }
        }
      `}</style>
      <aside
        className={`
          sidebar-mobile-fixed
          bg-white w-64 flex flex-col border-r border-gray-200
          transition-transform duration-300 ease-in-out
          z-[60] shadow-xl flex-shrink-0
          ${sidebarVisible ? "translate-x-0" : "-translate-x-full"}
          min-[1025px]:translate-x-0 min-[1025px]:shadow-none min-[1025px]:z-auto min-[1025px]:relative min-[1025px]:h-full
          ${!sidebarVisible && window.innerWidth <= 1024 ? 'hidden' : ''}
        `}
        style={{ 
          touchAction: 'auto',
          WebkitTouchCallout: 'default',
          WebkitUserSelect: 'auto',
          userSelect: 'auto',
          display: window.innerWidth <= 1024 && !sidebarVisible ? 'none' : undefined
        }}
      >
      <div className="flex items-center justify-between p-3 border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-0">
          <h2 className="text-base md:text-lg font-medium text-gray-700 truncate">
            <span className="hidden md:inline">Bienvenido: </span>
            <span className="md:hidden">Hola, </span>
            <span className="font-semibold text-gray-800">{userName || "Usuario"}</span>
          </h2>
        </div>
        <button
          className="max-[1180px]:flex min-[1181px]:hidden p-3 rounded-md hover:bg-gray-100 active:bg-gray-200 min-h-[48px] min-w-[48px] items-center justify-center ml-2 touch-manipulation"
          onClick={() => setSidebarVisible(false)}
          aria-label="Cerrar sidebar"
        >
          <PanelLeftClose size={20} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-3 px-1 sidebar-touch-area">
        {/* 🏠 Home — primer ítem para todos los usuarios */}
        <div className="px-3 py-1 mb-1">
          <button
            onClick={() => {
              navigate('/home');
              if (window.innerWidth < 768) {
                setTimeout(() => setSidebarVisible(false), 100);
              }
            }}
            className={`group flex items-center gap-3 w-full text-left px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 relative min-h-[48px] touch-manipulation ${
              location.pathname === '/home'
                ? 'bg-gradient-to-r from-blue-50 to-blue-50/50 text-blue-700'
                : 'text-gray-700 hover:bg-gray-50/80 active:bg-gray-100/80'
            }`}
          >
            <House
              size={20}
              strokeWidth={1.5}
              className={`transition-colors ${location.pathname === '/home' ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}
            />
            <span>Home</span>
            {location.pathname === '/home' && (
              <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-blue-600" />
            )}
          </button>
        </div>

        {/* Dashboard — solo admin */}
        {onBackToDashboard && isAdmin && (
          <div className="px-3 py-1 mb-1">
            <button
              onClick={() => {
                onBackToDashboard();
                if (window.innerWidth < 768) {
                  setTimeout(() => setSidebarVisible(false), 100);
                }
              }}
              className={`group flex items-center gap-3 w-full text-left px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 relative min-h-[48px] touch-manipulation ${
                isActiveSection('dashboard')
                  ? 'bg-gradient-to-r from-blue-50 to-blue-50/50 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-50/80 active:bg-gray-100/80'
              }`}
            >
              <LayoutDashboard 
                size={20} 
                strokeWidth={1.5}
                className={`transition-colors ${isActiveSection('dashboard') ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`} 
              />
              <span>Dashboard</span>
              {isActiveSection('dashboard') && (
                <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-blue-600" />
              )}
            </button>
          </div>
        )}

        {/* TICKET NVDLP4 - Micro Cursos del Course Lab Builder */}
        {microCoursesEnabled && (
          <div className="px-3 py-1 mb-1">
            <button
              onClick={() => {
                navigate('/dashboard_cursos_disponibles');
                if (window.innerWidth < 768) {
                  setTimeout(() => setSidebarVisible(false), 100);
                }
              }}
              className={`group flex items-center gap-3 w-full text-left px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 relative min-h-[48px] touch-manipulation ${
                location.pathname === '/dashboard_cursos_disponibles' || location.pathname.startsWith('/courses/')
                  ? 'bg-gradient-to-r from-blue-50 to-blue-50/50 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-50/80 active:bg-gray-100/80'
              }`}
            >
              <GraduationCap 
                size={20} 
                strokeWidth={1.5}
                className={`transition-colors ${
                  location.pathname === '/dashboard_cursos_disponibles' || location.pathname.startsWith('/courses/')
                    ? 'text-blue-600' 
                    : 'text-gray-400 group-hover:text-gray-600'
                }`} 
              />
              <span>Micro Cursos</span>
              {(location.pathname === '/dashboard_cursos_disponibles' || location.pathname.startsWith('/courses/')) && (
                <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-blue-600" />
              )}
            </button>
          </div>
        )}

        {/* Programas - Cursos Grupales de 18 Semanas */}
        {groupCoursesEnabled && (
          <div className="px-3 py-1 mb-1">
            <button
              onClick={() => {
                navigate('/cursos-grupales');
                if (window.innerWidth < 768) {
                  setTimeout(() => setSidebarVisible(false), 100);
                }
              }}
              className={`group flex items-center gap-3 w-full text-left px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 relative min-h-[48px] touch-manipulation ${
                location.pathname === '/cursos-grupales' || location.pathname.startsWith('/group-courses/')
                  ? 'bg-gradient-to-r from-green-50 to-green-50/50 text-green-700'
                  : 'text-gray-700 hover:bg-gray-50/80 active:bg-gray-100/80'
              }`}
            >
              <BookOpen 
                size={20} 
                strokeWidth={1.5}
                className={`transition-colors ${
                  location.pathname === '/cursos-grupales' || location.pathname.startsWith('/group-courses/')
                    ? 'text-green-600' 
                    : 'text-gray-400 group-hover:text-gray-600'
                }`} 
              />
              <span>Programas</span>
              {(location.pathname === '/cursos-grupales' || location.pathname.startsWith('/group-courses/')) && (
                <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-green-600" />
              )}
            </button>
          </div>
        )}

        {/* Mensajes del Docente - Para estudiantes */}
        {!isAdmin && (
          <div className="px-3 py-1 mb-1">
            <button
              onClick={() => {
                navigate('/epl/messages');
                if (window.innerWidth < 768) {
                  setTimeout(() => setSidebarVisible(false), 100);
                }
              }}
              className={`group flex items-center gap-3 w-full text-left px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 relative min-h-[48px] touch-manipulation ${
                location.pathname === '/epl/messages'
                  ? 'bg-gradient-to-r from-purple-50 to-purple-50/50 text-purple-700'
                  : 'text-gray-700 hover:bg-gray-50/80 active:bg-gray-100/80'
              }`}
            >
              <div className="relative">
                <Mail 
                  size={20} 
                  strokeWidth={1.5}
                  className={`transition-colors ${location.pathname === '/epl/messages' ? 'text-purple-600' : 'text-gray-400 group-hover:text-gray-600'}`} 
                />
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-purple-600 text-white text-[10px] font-semibold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              <span>Mensajes</span>
              {unreadCount > 0 && (
                <span className="ml-auto bg-purple-600 text-white text-[11px] font-semibold rounded-full px-2 py-0.5">
                  {unreadCount}
                </span>
              )}
              {location.pathname === '/epl/messages' && (
                <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-purple-600" />
              )}
            </button>
          </div>
        )}

        {/* Mensajes Enviados - Para docentes/admins */}
        {isAdmin && (
          <div className="px-3 py-1 mb-1">
            <button
              onClick={() => {
                navigate('/epl/teacher/messages-sent');
                if (window.innerWidth < 768) {
                  setTimeout(() => setSidebarVisible(false), 100);
                }
              }}
              className={`group flex items-center gap-3 w-full text-left px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 relative min-h-[48px] touch-manipulation ${
                location.pathname === '/epl/teacher/messages-sent'
                  ? 'bg-gradient-to-r from-purple-50 to-purple-50/50 text-purple-700'
                  : 'text-gray-700 hover:bg-gray-50/80 active:bg-gray-100/80'
              }`}
            >
              <Send 
                size={20} 
                strokeWidth={1.5}
                className={`transition-colors ${location.pathname === '/epl/teacher/messages-sent' ? 'text-purple-600' : 'text-gray-400 group-hover:text-gray-600'}`} 
              />
              <span>Mensajes Enviados</span>
              {location.pathname === '/epl/teacher/messages-sent' && (
                <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-purple-600" />
              )}
            </button>
          </div>
        )}

        {/* Dashboard Docente - Monitoreo Multi-Grupo */}
        {(isAdmin || isTeacher) && (
          <div className="px-3 py-1 mb-1">
            <button
              onClick={() => {
                navigate('/teaching/dashboard');
                if (window.innerWidth < 768) {
                  setTimeout(() => setSidebarVisible(false), 100);
                }
              }}
              className={`group flex items-center gap-3 w-full text-left px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 relative min-h-[48px] touch-manipulation ${
                location.pathname === '/teaching/dashboard'
                  ? 'bg-gradient-to-r from-indigo-50 to-indigo-50/50 text-indigo-700'
                  : 'text-gray-700 hover:bg-gray-50/80 active:bg-gray-100/80'
              }`}
            >
              <BarChart3 
                size={20} 
                strokeWidth={1.5}
                className={`transition-colors ${location.pathname === '/teaching/dashboard' ? 'text-indigo-600' : 'text-gray-400 group-hover:text-gray-600'}`} 
              />
              <span>Dashboard Docente</span>
              {location.pathname === '/teaching/dashboard' && (
                <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-indigo-600" />
              )}
            </button>
          </div>
        )}

        {/* 🔖 Accesos Rápidos - Solo aparecen cuando el usuario ha iniciado algo */}
        {quickAccess.length > 0 && (
          <div className="px-4 py-2 mb-2">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 px-3">
              Acceso Rápido
            </div>
            {quickAccess.map((access) => {
              const IconComponent = LucideIcons[access.icon] || LucideIcons.Lightbulb;
              const isActive = location.pathname === access.path;
              
              return (
                <button
                  key={access.id}
                  onClick={() => {
                    navigate(access.path);
                    if (window.innerWidth < 768) {
                      setTimeout(() => setSidebarVisible(false), 100);
                    }
                  }}
                  className={`flex items-center gap-3 w-full text-left px-3 py-3 text-sm font-medium rounded-lg transition-all duration-150 relative min-h-[40px] border-l-0 touch-manipulation active:scale-95 mb-1 ${
                    isActive
                      ? 'bg-indigo-50 text-indigo-700 shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 active:bg-gray-100'
                  }`}
                >
                  <IconComponent size={18} className={isActive ? 'text-indigo-600' : 'text-gray-400'} />
                  <span className="truncate">{access.title}</span>
                  <span className="text-xs text-gray-400 ml-auto">
                    {new Date(access.timestamp).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        
        {/* ASISTENTES DESHABILITADOS - Solo EPL y VRT activos */}
        {/* Array vacío - Antiguos asistentes (PMFAST, CRISIS, COMUNICA) eliminados */}
        {[].map((assistantKey) => {
            const assistantConfig = asistentesConfig[assistantKey];
            const assistantChats = allUserChats[assistantKey] || [];
            const hasReachedLimit = assistantChats.length >= MAX_CHATS_PER_ASSISTANT;
            const isAssistantHeaderDisabled = loading || !isAuthenticated;
            const isDisabledNewChatButton = isCreatingNewChat || hasReachedLimit || loading || !isAuthenticated || !assistantConfig?.canCreateChat;
            const selectedColorClasses = assistantConfig?.color 
                ? getAssistantColorClasses(assistantConfig.color)
                : 'bg-blue-100 text-blue-800';

            // Handler especial por tipo de asistente
            const handleClick = () => {
              if (assistantKey === 'innovafast') {
                // INNOVAFAST: Navegar a la metodología de 5 etapas
                navigate('/innovafast');
                if (window.innerWidth < 768) setSidebarVisible(false);
              } else if (assistantConfig?.isLab && assistantConfig?.route) {
                // EPL LAB BUILDER: Navegar a sistema de laboratorios
                navigate(assistantConfig.route);
                if (window.innerWidth < 768) setSidebarVisible(false);
              } else {
                // Otros asistentes: Comportamiento de chat tradicional
                const isCurrentlyExpanded = expanded[assistantKey];
                setExpanded(prev => ({ ...prev, [assistantKey]: !isCurrentlyExpanded }));
                if (!isCurrentlyExpanded) {
                  if (selectedAssistant !== assistantKey || selectedChatId !== null) {
                    handleSelectChat(null, assistantKey);
                  }
                } else {
                  if (selectedAssistant === assistantKey && selectedChatId !== null) {
                    handleSelectChat(null, assistantKey);
                  }
                }
              }
            };

            return (
              <div 
                key={assistantKey} 
                className="mb-2"
              >
                <button
                  onClick={handleClick}
                  className={`flex items-center justify-between w-full px-4 py-3 text-sm font-semibold transition-all duration-200 rounded-lg relative group ${
                    (selectedAssistant === assistantKey && selectedChatId === null) || isActiveSection('assistant', assistantKey)
                      ? 'bg-emerald-50 text-emerald-700 shadow-sm border-l-4 border-emerald-600'
                      : "text-gray-700 hover:bg-gray-50 hover:text-gray-900 hover:shadow-sm"
                  } ${isAssistantHeaderDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                  disabled={isAssistantHeaderDisabled}
                >
                  <div className="flex items-center gap-3">
                    {asistentesConfig[assistantKey].icon && LucideIcons[asistentesConfig[assistantKey].icon] ? (
                      React.createElement(LucideIcons[asistentesConfig[assistantKey].icon], { 
                        size: 20,
                        className: (selectedAssistant === assistantKey && selectedChatId === null) || isActiveSection('assistant', assistantKey)
                          ? 'text-emerald-600'
                          : 'text-gray-500 group-hover:text-gray-700'
                      })
                    ) : (
                      <MessageSquare 
                        size={20} 
                        className={(selectedAssistant === assistantKey && selectedChatId === null) || isActiveSection('assistant', assistantKey)
                          ? 'text-emerald-600'
                          : 'text-gray-500 group-hover:text-gray-700'
                        } 
                      />
                    )}
                    {asistentesConfig[assistantKey].name}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Badge "Nuevo" rediseñado con pill style */}
                    {asistentesConfig[assistantKey].showBadge && (
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full transition-colors ${
                        (selectedAssistant === assistantKey && selectedChatId === null) || isActiveSection('assistant', assistantKey)
                          ? 'bg-blue-600 text-white'
                          : 'bg-blue-100 text-blue-700 group-hover:bg-blue-200'
                      }`}>
                        Nuevo
                      </span>
                    )}
                    {assistantKey !== 'innovafast' && (
                      expanded[assistantKey] ? (
                        <ChevronDown 
                          size={16} 
                          className={
                            (selectedAssistant === assistantKey && selectedChatId === null) || isActiveSection('assistant', assistantKey)
                              ? 'text-emerald-600'
                              : 'text-gray-400 group-hover:text-gray-600'
                          } 
                        />
                      ) : (
                        <ChevronRight 
                          size={16}
                          className={
                            (selectedAssistant === assistantKey && selectedChatId === null) || isActiveSection('assistant', assistantKey)
                              ? 'text-emerald-600'
                              : 'text-gray-400 group-hover:text-gray-600'
                          }
                        />
                      )
                    )}
                  </div>
                </button>
                {/* Render chats y botón Nuevo chat solo si no es innovafast */}
                {assistantKey !== 'innovafast' && expanded[assistantKey] && (
                  <div className="ml-4 mt-1">
                    {(assistantChats).map((chat) => (
                      <div
                        key={chat._id}
                        className={`flex items-center justify-between pr-2 rounded-lg transition-all duration-200 mb-1 relative ${
                          selectedChatId === chat._id 
                            ? "bg-emerald-50 shadow-sm border-l-3 border-emerald-500" 
                            : "hover:bg-gray-50 hover:shadow-sm"
                        }`}
                        onMouseEnter={() => setHovered({ section: assistantKey, id: chat._id })}
                        onMouseLeave={() => setHovered({ section: null, id: null })}
                        onTouchStart={(e) => handleTouchStart(e, chat, assistantKey)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={(e) => handleTouchEnd(e, chat, assistantKey)}
                      >
                        {editingChatId === chat._id && window.innerWidth >= 768 ? (
                          <input
                            type="text"
                            value={newChatName}
                            onChange={(e) => setNewChatName(e.target.value)}
                            onBlur={() => submitRename(chat._id, assistantKey)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                submitRename(chat._id, assistantKey);
                              }
                            }}
                            className="flex-1 px-3 py-1 text-xs text-gray-800 rounded-md bg-white border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 my-1"
                            autoFocus
                          />
                        ) : (
                          <button
                            onClick={() => window.innerWidth >= 768 && handleChatSelection(chat._id, assistantKey)}
                            className={`flex-1 text-left px-3 py-2.5 text-xs font-medium truncate transition-colors ${
                              selectedChatId === chat._id 
                                ? "text-emerald-700" 
                                : "text-gray-600 hover:text-gray-900"
                            }`}
                            style={{
                              WebkitTapHighlightColor: 'transparent',
                              WebkitTouchCallout: 'none',
                              userSelect: 'none'
                            }}
                          >
                            {chat.nombre || `Chat sin nombre (${new Date(chat.fechaCreacion).toLocaleDateString()})`}
                          </button>
                        )}

                        {window.innerWidth >= 768 && (hovered.section === assistantKey && hovered.id === chat._id) && editingChatId !== chat._id && (
                          <div className="flex gap-1">
                            <button
                              onClick={() => startRename(chat._id, chat.nombre || `Chat sin nombre`)}
                              className="p-1 rounded-md hover:bg-gray-200"
                              title="Renombrar chat"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteChat(chat._id)}
                              className="p-1 rounded-md hover:bg-red-100 text-red-600"
                              title="Eliminar chat"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}

                    <button
                      onClick={() => handleNewChatClick(assistantKey)}
                      className={`
                        flex items-center gap-2 text-sm font-medium ml-4 mt-2 px-3 py-3 md:py-2
                        rounded-lg focus:outline-none transition-all duration-150 min-h-[44px]
                        ${isDisabledNewChatButton 
                          ? "opacity-50 cursor-not-allowed text-gray-400" 
                          : "text-blue-700 bg-blue-50 hover:bg-blue-100 hover:shadow-sm border border-blue-200 active:scale-95"
                        }
                      `}
                      disabled={isDisabledNewChatButton}
                    >
                      <MessageSquarePlus size={18} strokeWidth={2} className="text-blue-600" />
                      Nuevo chat
                    </button>
                
                    {hasReachedLimit && (
                      <p className="text-red-500 text-xs mt-1 ml-4">
                        Máx. {MAX_CHATS_PER_ASSISTANT} chats alcanzados.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      <div className="px-3 pt-3 pb-4 border-t border-gray-100 flex-shrink-0 sidebar-touch-area">
        {/* 🆕 Botón de EPL Lab Builder - SOLO para admins */}
        {isAdmin && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              // Cerrar sidebar inmediatamente en móvil
              if (window.innerWidth < 768) {
                setSidebarVisible(false);
                setTimeout(() => {
                  navigate('/epl/admin/labs');
                }, 100);
              } else {
                navigate('/epl/admin/labs');
              }
            }}
            className={`group flex items-center gap-3 w-full px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 min-h-[48px] touch-manipulation mb-1 ${
              isActiveSection('eplbuilder') 
                ? 'bg-gradient-to-r from-blue-50 to-blue-50/50 text-blue-700' 
                : 'text-gray-700 hover:bg-gray-50/80 active:bg-gray-100/80'
            }`}
          >
            <LucideIcons.Beaker 
              size={20} 
              strokeWidth={1.5}
              className={`transition-colors ${isActiveSection('eplbuilder') ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`} 
            />
            <span>EPL Lab Builder</span>
            {isActiveSection('eplbuilder') && (
              <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-blue-600" />
            )}
          </button>
        )}

        {/* 🤖 VRT Lab Builder - SOLO para admins */}
        {isAdmin && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              if (window.innerWidth < 768) {
                setSidebarVisible(false);
                setTimeout(() => {
                  navigate('/vertex/builder');
                }, 100);
              } else {
                navigate('/vertex/builder');
              }
            }}
            className={`group flex items-center gap-3 w-full px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 min-h-[48px] touch-manipulation mb-1 ${
              location.pathname === '/vertex/builder'
                ? 'bg-gradient-to-r from-indigo-50 to-indigo-50/50 text-indigo-700' 
                : 'text-gray-700 hover:bg-gray-50/80 active:bg-gray-100/80'
            }`}
          >
            <LucideIcons.Bot 
              size={20} 
              strokeWidth={1.5}
              className={`transition-colors ${location.pathname === '/vertex/builder' ? 'text-indigo-600' : 'text-gray-400 group-hover:text-gray-600'}`} 
            />
            <span>VRT Lab Builder</span>
            {location.pathname === '/vertex/builder' && (
              <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-indigo-600" />
            )}
          </button>
        )}

        {/* 📚 TICKET LTPKD7 - Course Builder - SOLO para admins/teachers */}
        {(isAdmin || isTeacher) && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              if (window.innerWidth < 768) {
                setSidebarVisible(false);
                setTimeout(() => {
                  navigate('/admin/course-builder');
                }, 100);
              } else {
                navigate('/admin/course-builder');
              }
            }}
            className={`group flex items-center gap-3 w-full px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 min-h-[48px] touch-manipulation mb-1 ${
              location.pathname === '/admin/course-builder' || location.pathname.startsWith('/admin/course-builder/')
                ? 'bg-gradient-to-r from-blue-50 to-blue-50/50 text-blue-700' 
                : 'text-gray-700 hover:bg-gray-50/80 active:bg-gray-100/80'
            }`}
          >
            <LucideIcons.BookOpen 
              size={20} 
              strokeWidth={1.5}
              className={`transition-colors ${
                location.pathname === '/admin/course-builder' || location.pathname.startsWith('/admin/course-builder/')
                  ? 'text-blue-600' 
                  : 'text-gray-400 group-hover:text-gray-600'
              }`} 
            />
            <span>Curso Lab Builder</span>
            {(location.pathname === '/admin/course-builder' || location.pathname.startsWith('/admin/course-builder/')) && (
              <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-blue-600" />
            )}
          </button>
        )}

        {/* 📚 GroupCourse Builder - SOLO para admins/teachers */}
        {(isAdmin || isTeacher) && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              if (window.innerWidth < 768) {
                setSidebarVisible(false);
                setTimeout(() => {
                  navigate('/admin/group-course-builder');
                }, 100);
              } else {
                navigate('/admin/group-course-builder');
              }
            }}
            className={`group flex items-center gap-3 w-full px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 min-h-[48px] touch-manipulation mb-1 ${
              location.pathname === '/admin/group-course-builder' || location.pathname.startsWith('/admin/group-course-builder/')
                ? 'bg-gradient-to-r from-purple-50 to-purple-50/50 text-purple-700' 
                : 'text-gray-700 hover:bg-gray-50/80 active:bg-gray-100/80'
            }`}
          >
            <LucideIcons.Users 
              size={20} 
              strokeWidth={1.5}
              className={`transition-colors ${
                location.pathname === '/admin/group-course-builder' || location.pathname.startsWith('/admin/group-course-builder/')
                  ? 'text-purple-600' 
                  : 'text-gray-400 group-hover:text-gray-600'
              }`} 
            />
            <span>Grupo Builder</span>
            {(location.pathname === '/admin/group-course-builder' || location.pathname.startsWith('/admin/group-course-builder/')) && (
              <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-purple-600" />
            )}
          </button>
        )}

        {/* Certificados - Para todos los usuarios autenticados */}
        {!isAdmin && (
          <button
            onClick={() => {
              navigate('/home/certificates');
              if (window.innerWidth < 768) {
                setTimeout(() => setSidebarVisible(false), 100);
              }
            }}
            className={`group flex items-center gap-3 w-full text-left px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 relative min-h-[48px] touch-manipulation mb-1 ${
              location.pathname.startsWith('/home/certificates')
                ? 'bg-cyan-50 text-cyan-700 border border-cyan-100'
                : 'text-gray-700 hover:bg-gray-50/80 active:bg-gray-100/80'
            }`}
          >
            <Award
              size={20}
              strokeWidth={1.5}
              className={`transition-colors ${location.pathname.startsWith('/home/certificates') ? 'text-cyan-600' : 'text-gray-400 group-hover:text-gray-600'}`}
            />
            <span>Certificados</span>
            {location.pathname.startsWith('/home/certificates') && (
              <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-cyan-500" />
            )}
          </button>
        )}

        {/* Config Admin - SOLO para admins */}
        {isAdmin && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              if (window.innerWidth < 768) {
                setSidebarVisible(false);
                setTimeout(() => {
                  navigate('/admin/config');
                }, 100);
              } else {
                navigate('/admin/config');
              }
            }}
            className="group flex items-center gap-3 w-full px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 min-h-[48px] touch-manipulation text-gray-700 hover:bg-gray-50/80 active:bg-gray-100/80 mb-1"
          >
            <Settings 
              size={20} 
              strokeWidth={1.5}
              className="text-gray-400 group-hover:text-gray-600 transition-colors" 
            />
            <span>Config Admin</span>
          </button>
        )}

        
        {/* Configuración y Perfil - SOLO para no-admins */}
        {!isAdmin && (
          <>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🔧 Configuración clicked');
              }}
              onTouchStart={(e) => {
                e.preventDefault();
              }}
              className="group flex items-center gap-3 w-full px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 min-h-[48px] touch-manipulation text-gray-700 hover:bg-gray-50/80 active:bg-gray-100/80 mb-1"
            >
              <Settings 
                size={20} 
                strokeWidth={1.5}
                className="text-gray-400 group-hover:text-gray-600 transition-colors" 
              />
              <span>Configuración</span>
            </button>
            
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('👤 Perfil clicked');
              }}
              onTouchStart={(e) => {
                e.preventDefault();
              }}
              className="group flex items-center gap-3 w-full px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 min-h-[48px] touch-manipulation text-gray-700 hover:bg-gray-50/80 active:bg-gray-100/80 mb-1"
            >
              <UserCircle 
                size={20} 
                strokeWidth={1.5}
                className="text-gray-400 group-hover:text-gray-600 transition-colors" 
              />
              <span>Perfil</span>
            </button>
          </>
        )}
        
        <div className="pt-2 mt-2 border-t border-gray-100">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (window.confirm("¿Estás seguro de que quieres cerrar sesión?")) {
                localStorage.removeItem("token");
                localStorage.removeItem("userEmail");
                // Limpiar borradores de ejercicios de programas
                Object.keys(localStorage)
                  .filter(k => k.startsWith('epl_exercise_draft_'))
                  .forEach(k => localStorage.removeItem(k));
                window.location.href = "/login";
              }
            }}
            onTouchStart={(e) => {
              e.preventDefault();
            }}
            className="group flex items-center gap-3 w-full px-4 py-3 text-sm font-normal rounded-xl transition-all duration-200 min-h-[48px] touch-manipulation text-gray-700 hover:bg-red-50/80 hover:text-red-600 active:bg-red-100/80"
          >
            <LogOut 
              size={20} 
              strokeWidth={1.5}
              className="text-gray-400 group-hover:text-red-600 transition-colors" 
            />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>

        {showContextMenu && (
          <ChatContextMenuModal
            chat={selectedChatForContext.chat}
            assistantKey={selectedChatForContext.assistantKey}
            onDelete={() => {
              if (window.confirm("¿Estás seguro de que quieres eliminar este chat?")) {
                handleDeleteChat(selectedChatForContext.chat._id);
              }
              closeContextMenu();
            }}
            onClose={closeContextMenu}
          />
        )}
      </aside>

      {/* Backdrop/Overlay móvil - Cierra sidebar al tocar fuera (iOS + Android) */}
      {sidebarVisible && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[50] min-[1025px]:hidden"
          onClick={() => setSidebarVisible(false)}
          style={{
            top: 'var(--dashboard-header-height-mobile)',
            height: 'calc(100dvh - var(--dashboard-header-height-mobile))',
            touchAction: 'auto',
            WebkitTapHighlightColor: 'transparent',
            WebkitTouchCallout: 'none',
            cursor: 'pointer'
          }}
          aria-label="Cerrar menú"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setSidebarVisible(false);
            }
          }}
        />
      )}
    </>
  );
};

export default SidebarAsistentes;